/**
 * FSRS-4.5 (Free Spaced Repetition Scheduler)
 *
 * Planovac opakovani. Pro kazdou kartu drzi dve cisla:
 *   - stability  ... za kolik dni klesne pravdepodobnost vybaveni na cilovou retenci
 *   - difficulty ... jak je ta konkretni karta pro tebe obtizna (1-10)
 *
 * Z nich se spocita `due`. Zadny fixni nasobic intervalu jako u starych SM-2 systemu.
 *
 * Vlastni implementace zamerne: zadna zavislost, plna kontrola nad parametry
 * a moznost je ve fazi 4 pretrenovat na vlastnich datech z tabulky `reviews`.
 *
 * Referencni popis algoritmu: https://github.com/open-spaced-repetition/fsrs4anki/wiki
 */

/** Hodnoceni odpovedi uzivatelem. */
export const Rating = {
  Again: 1,
  Hard: 2,
  Good: 3,
  Easy: 4,
} as const;

export type Rating = (typeof Rating)[keyof typeof Rating];

/** Stav karty v zivotnim cyklu uceni. */
export const CardState = {
  /** Jeste nikdy nezkousena. */
  New: 'new',
  /** V kratkodobem uceni (dnes chybne, doucuje se v minutach). */
  Learning: 'learning',
  /** Zafixovana, opakuje se v dnech. */
  Review: 'review',
  /** Byla zafixovana, ale zapomnel jsi ji -> doucuje se znovu. */
  Relearning: 'relearning',
} as const;

export type CardState = (typeof CardState)[keyof typeof CardState];

/** SRS stav jedne karty. Presne tohle se uklada do DB. */
export interface SchedulerState {
  state: CardState;
  /** Ve dnech. `null` u nove karty. */
  stability: number | null;
  /** 1-10. `null` u nove karty. */
  difficulty: number | null;
  /** Kdy je karta pristi na rade. */
  due: Date;
  /** Cas posledniho zkouseni; `null` u nove karty. */
  lastReviewedAt: Date | null;
  /** Celkovy pocet zkouseni. */
  reps: number;
  /** Kolikrat jsi ji zapomnel po zafixovani (Again ve stavu review). */
  lapses: number;
}

export interface SchedulerConfig {
  /** Cilova retence, 0.70-0.98. Nizsi = delsi intervaly, mene prace, vic zapominani. */
  requestRetention: number;
  /** Strop intervalu ve dnech, aby karta nezmizela na 10 let. */
  maximumInterval: number;
  /** Kroky doucovani nove karty v minutach (odpoved Again / Hard). */
  learningStepsMinutes: number[];
  /** Kroky doucovani zapomenute karty v minutach. */
  relearningStepsMinutes: number[];
  /** 17 vah FSRS-4.5. */
  weights: readonly number[];
}

/** Vychozi vahy FSRS-4.5 (natrenovane na velkem verejnem datasetu z Anki). */
export const DEFAULT_WEIGHTS = [
  0.4872, 1.4003, 3.7145, 13.8206, 5.1618, 1.2298, 0.8975, 0.031, 1.6474, 0.1367,
  1.0461, 2.1072, 0.0793, 0.3246, 1.587, 0.2272, 2.8755,
] as const;

export const DEFAULT_CONFIG: SchedulerConfig = {
  requestRetention: 0.9,
  maximumInterval: 365 * 3,
  learningStepsMinutes: [1, 10],
  relearningStepsMinutes: [10],
  weights: DEFAULT_WEIGHTS,
};

/** Konstanty krivky zapominani FSRS-4.5 (power-law forgetting curve). */
const DECAY = -0.5;
const FACTOR = 19 / 81;

const MIN_DIFFICULTY = 1;
const MAX_DIFFICULTY = 10;
const MIN_STABILITY = 0.01;

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * Pravdepodobnost, ze si kartu vybavis, kdyz je stabilita `stability`
 * a od posledniho zkouseni proslo `elapsedDays` dni.
 */
export function retrievability(elapsedDays: number, stability: number): number {
  if (stability <= 0) return 0;
  return Math.pow(1 + (FACTOR * Math.max(elapsedDays, 0)) / stability, DECAY);
}

/** Interval ve dnech, po kterem retence spadne prave na `requestRetention`. */
function intervalFromStability(stability: number, config: SchedulerConfig): number {
  const raw = (stability / FACTOR) * (Math.pow(config.requestRetention, 1 / DECAY) - 1);
  return clamp(Math.round(raw), 1, config.maximumInterval);
}

function initialStability(rating: Rating, w: readonly number[]): number {
  return Math.max(w[rating - 1], MIN_STABILITY);
}

function initialDifficulty(rating: Rating, w: readonly number[]): number {
  return clamp(w[4] - Math.exp(w[5] * (rating - 1)) + 1, MIN_DIFFICULTY, MAX_DIFFICULTY);
}

/** Obtiznost se posouva podle hodnoceni a tahne se zpet k obtiznosti "Easy" karty (mean reversion). */
function nextDifficulty(difficulty: number, rating: Rating, w: readonly number[]): number {
  const delta = difficulty - w[6] * (rating - 3);
  const reverted = w[7] * initialDifficulty(Rating.Easy, w) + (1 - w[7]) * delta;
  return clamp(reverted, MIN_DIFFICULTY, MAX_DIFFICULTY);
}

/** Nova stabilita po uspesnem vybaveni (Hard/Good/Easy). */
function stabilityAfterRecall(
  stability: number,
  difficulty: number,
  r: number,
  rating: Rating,
  w: readonly number[],
): number {
  const hardPenalty = rating === Rating.Hard ? w[15] : 1;
  const easyBonus = rating === Rating.Easy ? w[16] : 1;

  const growth =
    1 +
    Math.exp(w[8]) *
      (11 - difficulty) *
      Math.pow(stability, -w[9]) *
      (Math.exp(w[10] * (1 - r)) - 1) *
      hardPenalty *
      easyBonus;

  return Math.max(stability * growth, MIN_STABILITY);
}

/** Nova stabilita po zapomenuti (Again). Vzdy vyrazne nizsi, ale ne uplne od nuly. */
function stabilityAfterForget(
  stability: number,
  difficulty: number,
  r: number,
  w: readonly number[],
): number {
  const next =
    w[11] *
    Math.pow(difficulty, -w[12]) *
    (Math.pow(stability + 1, w[13]) - 1) *
    Math.exp(w[14] * (1 - r));

  // Nova stabilita nikdy neprekroci tu puvodni - zapomenuti nemuze kartu "vylepsit".
  return clamp(next, MIN_STABILITY, stability);
}

/** Vytvori stav pro dosud nezkousenou kartu. */
export function newCardState(now: Date = new Date()): SchedulerState {
  return {
    state: CardState.New,
    stability: null,
    difficulty: null,
    due: now,
    lastReviewedAt: null,
    reps: 0,
    lapses: 0,
  };
}

export interface ReviewOutcome {
  /** Novy stav karty k zapsani do DB. */
  next: SchedulerState;
  /** Interval do dalsiho zkouseni v dnech (0 = jeste dnes, v ramci doucovani). */
  intervalDays: number;
  /** Jaka byla odhadovana pravdepodobnost vybaveni pred touto odpovedi (0-1, `null` u nove karty). */
  retrievabilityBefore: number | null;
}

/**
 * Jadro planovace: vezme aktualni stav karty + hodnoceni a vrati novy stav.
 *
 * Cista funkce - nic nemutuje, nic necte z hodin krome predaneho `now`.
 * Diky tomu je testovatelna a diky tomu funguje offline rezim: odpoved
 * prijata s hodinovym zpozdenim se prepocita ke svemu skutecnemu casu.
 */
export function scheduleReview(
  current: SchedulerState,
  rating: Rating,
  now: Date = new Date(),
  config: SchedulerConfig = DEFAULT_CONFIG,
): ReviewOutcome {
  const w = config.weights;
  const reps = current.reps + 1;

  // --- Prvni zkouseni karty ---
  if (current.state === CardState.New || current.stability === null || current.difficulty === null) {
    const stability = initialStability(rating, w);
    const difficulty = initialDifficulty(rating, w);

    if (rating === Rating.Again || rating === Rating.Hard) {
      // Zustava v kratkodobem uceni, vraci se za par minut.
      const stepIndex = rating === Rating.Again ? 0 : Math.min(1, config.learningStepsMinutes.length - 1);
      const minutes = config.learningStepsMinutes[stepIndex] ?? 10;
      return {
        next: {
          state: CardState.Learning,
          stability,
          difficulty,
          due: new Date(now.getTime() + minutes * MINUTE_MS),
          lastReviewedAt: now,
          reps,
          lapses: current.lapses,
        },
        intervalDays: 0,
        retrievabilityBefore: null,
      };
    }

    const intervalDays = intervalFromStability(stability, config);
    return {
      next: {
        state: CardState.Review,
        stability,
        difficulty,
        due: new Date(now.getTime() + intervalDays * DAY_MS),
        lastReviewedAt: now,
        reps,
        lapses: current.lapses,
      },
      intervalDays,
      retrievabilityBefore: null,
    };
  }

  // --- Opakovane zkouseni ---
  const elapsedMs = current.lastReviewedAt ? now.getTime() - current.lastReviewedAt.getTime() : 0;
  const elapsedDays = Math.max(elapsedMs / DAY_MS, 0);
  const r = retrievability(elapsedDays, current.stability);

  const difficulty = nextDifficulty(current.difficulty, rating, w);
  const isSameDay = elapsedDays < 1 && current.state !== CardState.Review;

  let stability: number;
  if (rating === Rating.Again) {
    stability = stabilityAfterForget(current.stability, current.difficulty, r, w);
  } else if (isSameDay) {
    // Doucovani v ramci jednoho dne stabilitu skoro nemeni - pamet je jen kratkodoba.
    stability = current.stability;
  } else {
    stability = stabilityAfterRecall(current.stability, current.difficulty, r, rating, w);
  }

  // Again u zafixovane karty = lapse -> doucovani v minutach.
  if (rating === Rating.Again) {
    const minutes = config.relearningStepsMinutes[0] ?? 10;
    const wasInReview = current.state === CardState.Review;
    return {
      next: {
        state: CardState.Relearning,
        stability,
        difficulty,
        due: new Date(now.getTime() + minutes * MINUTE_MS),
        lastReviewedAt: now,
        reps,
        lapses: current.lapses + (wasInReview ? 1 : 0),
      },
      intervalDays: 0,
      retrievabilityBefore: r,
    };
  }

  // Hard behem doucovani jeste kartu nepropousti do dennich intervalu.
  if (
    (current.state === CardState.Learning || current.state === CardState.Relearning) &&
    rating === Rating.Hard
  ) {
    const steps =
      current.state === CardState.Learning ? config.learningStepsMinutes : config.relearningStepsMinutes;
    const minutes = steps[steps.length - 1] ?? 10;
    return {
      next: {
        state: current.state,
        stability,
        difficulty,
        due: new Date(now.getTime() + minutes * MINUTE_MS),
        lastReviewedAt: now,
        reps,
        lapses: current.lapses,
      },
      intervalDays: 0,
      retrievabilityBefore: r,
    };
  }

  const intervalDays = intervalFromStability(stability, config);
  return {
    next: {
      state: CardState.Review,
      stability,
      difficulty,
      due: new Date(now.getTime() + intervalDays * DAY_MS),
      lastReviewedAt: now,
      reps,
      lapses: current.lapses,
    },
    intervalDays,
    retrievabilityBefore: r,
  };
}

/**
 * Nahled intervalu pro vsechna ctyri tlacitka - aby UI mohlo ukazat
 * "Good = za 12 dni". Nemeni nic, jen pocita.
 */
export function previewIntervals(
  current: SchedulerState,
  now: Date = new Date(),
  config: SchedulerConfig = DEFAULT_CONFIG,
): Record<Rating, number> {
  return {
    [Rating.Again]: scheduleReview(current, Rating.Again, now, config).intervalDays,
    [Rating.Hard]: scheduleReview(current, Rating.Hard, now, config).intervalDays,
    [Rating.Good]: scheduleReview(current, Rating.Good, now, config).intervalDays,
    [Rating.Easy]: scheduleReview(current, Rating.Easy, now, config).intervalDays,
  };
}
